/**
 * Card data — DESIGN.md §7.1, §7.2, §13.2.
 *
 * `useTaskList` is the R19 surface: `useInfiniteQuery` over `POST
 * /tasks/query`, 100 rows a page, fetched as the viewport approaches the end.
 * Everything else here is a mutation that invalidates it.
 *
 * Mutations follow one rule (§13.2): optimistic only for the high-frequency
 * actions — status, complete, assignee — where the round trip is what makes a
 * board feel slow. Everything else waits for the server, because a rollback
 * the user notices is worse than 80 ms of latency.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { toast } from 'sonner';
import type {
  AddCollaboratorRequest,
  CreateTaskRequest,
  Page,
  SetTaskProjectsRequest,
  TaskDetail,
  TaskRef,
  TaskSummary,
  UpdateMetadataRequest,
  UpdateTaskRequest,
  ViewConfig,
} from '@slate/shared';
import { ApiError, http } from '@/lib/api';
import { queryKeys } from '@/lib/keys';

type TaskPage = Page<TaskSummary>;

export function useTaskList(config: ViewConfig, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.taskQuery(config),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      http.post<TaskPage>('/tasks/query', { config, cursor: pageParam, limit: 100 }),
    getNextPageParam: (last) => last.cursor.next,
    enabled: options.enabled ?? true,
  });
}

/** Flatten the pages into rows, plus the header's honest total (§7.2). */
export function listRows(data: InfiniteData<TaskPage> | undefined): {
  items: TaskSummary[];
  total: number;
} {
  if (!data) return { items: [], total: 0 };
  return {
    items: data.pages.flatMap((page) => page.items),
    total: data.pages[0]?.totalCount ?? 0,
  };
}

export function useTask(id: string | null): UseQueryResult<TaskDetail, Error> {
  return useQuery({
    queryKey: queryKeys.task(id ?? 'none'),
    queryFn: () => http.get<TaskDetail>(`/tasks/${id!}`),
    enabled: Boolean(id),
  });
}

export function useTaskByNumber(number: number | null): UseQueryResult<TaskDetail, Error> {
  return useQuery({
    queryKey: queryKeys.taskByNumber(number ?? 0),
    queryFn: () => http.get<TaskDetail>(`/tasks/by-number/${number!}`),
    enabled: Boolean(number),
  });
}

/**
 * Invalidate everything a card change can affect. Deliberately broad: the
 * alternative is surgical updates to every cached list a card might appear in,
 * which is exactly the state-merging §15/D8 rules out ("correctness over
 * cleverness at this scale").
 */
export function useInvalidateTasks(): (taskId?: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (taskId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
      queryClient.invalidateQueries({ queryKey: queryKeys.projects }),
      taskId
        ? queryClient.invalidateQueries({ queryKey: queryKeys.task(taskId) })
        : Promise.resolve(),
    ]);
  };
}

/**
 * Create a card.
 *
 * Creating is not opening (VIEW-6): a person adding five cards in a row does
 * not want the panel for each one, so the new card is *offered* in the toast
 * rather than shoved on screen.
 *
 * `announce: false` for the callers where the new card lands *in view* — a
 * subtask joins the checklist the person is looking at, and a toast offering
 * to navigate away from the parent is noise, not confirmation. Errors are
 * still toasted: a failure has nowhere else to show.
 */
/**
 * The routes that render a side peek, so #23 can open one instead of leaving
 * the list. Route ids rather than paths — a path match would break the moment
 * a route gained a parameter.
 */
const PEEK_ROUTES = new Set(['/', '/projects/$key']);

export function useCreateTask({
  announce = true,
  open = announce,
}: { announce?: boolean; open?: boolean } = {}) {
  const invalidate = useInvalidateTasks();
  const navigate = useNavigate();
  const routeId = useRouterState({ select: (state) => state.matches.at(-1)?.routeId ?? '' });
  return useMutation({
    mutationFn: (input: CreateTaskRequest) => http.post<TaskDetail>('/tasks', input),
    /*
     * #90 — the card is announced first and the lists catch up behind it.
     *
     * This was `await invalidate(task.id)` *before* the toast or the navigate,
     * so clicking + bought a POST and then a full refetch of the task list and
     * the project list before anything at all moved on screen. At ~240 ms per
     * round trip from Australia to us-central1 that is half a second of a
     * composer that looks like it ignored you — Luke's "little indication
     * anything is happening".
     *
     * Nothing here needs the refetch to have landed: the new card came back
     * from the POST, so the peek opens on data we already hold.
     */
    onSuccess: (task) => {
      invalidate(task.id).catch(() => {
        /* Focus, SSE and the next query refetch this anyway (D8). */
      });
      if (!announce) return;
      if (open) {
        /*
         * #23 — the new card opens with the caret in its description, because
         * a title alone is rarely the whole thought.
         *
         * The *peek*, not the full page, wherever the reader already has a
         * list in front of them: Brad's correction on the card is that the
         * list should stay visible behind it. Only somewhere with no peek to
         * open — search, the inbox — does this fall through to `/card/:num`,
         * because the alternative there is nothing happening at all.
         *
         * This reverses VIEW-6, which deliberately did *not* open the card
         * ("a person adding five cards in a row does not want five panels")
         * and offered it in the toast instead. Rapid entry now costs an Escape
         * and a click back to the composer; Luke asked for this on #23 and it
         * is recorded there.
         */
        if (PEEK_ROUTES.has(routeId)) {
          void navigate({
            to: '.',
            search: (previous: Record<string, unknown>) => ({
              ...previous,
              card: task.number,
              focus: 'description' as const,
            }),
          });
          return;
        }
        void navigate({
          to: '/card/$number',
          params: { number: String(task.number) },
          search: { focus: 'description' as const },
        });
        return;
      }
      toast.success(`Created #${task.number}`, {
        action: {
          label: 'Open',
          onClick: () =>
            void navigate({ to: '/card/$number', params: { number: String(task.number) } }),
        },
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Field updates carry `lastSeenUpdatedAt` so a concurrent edit returns 409
 * rather than silently overwriting (§6.2). The 409 handler refetches and tells
 * the user what happened — that is the whole point of the status code.
 */
export function useUpdateTask(taskId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (input: UpdateTaskRequest) => http.patch<TaskDetail>(`/tasks/${taskId}`, input),
    onSuccess: async (task) => {
      queryClient.setQueryData(queryKeys.task(taskId), task);
      await invalidate();
    },
    onError: async (error: Error) => {
      if (error instanceof ApiError && error.isStale) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.task(taskId) });
        toast.error('Someone else edited this card — reloaded their version');
        return;
      }
      toast.error(error.message);
    },
  });
}

/**
 * Complete / reopen — the highest-frequency action in the product, and the one
 * §13.2 names first for optimistic treatment. A blocked card returns 409 with
 * the blockers, which the caller turns into the §6.5 confirmation.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({
      taskId,
      completed,
      force = false,
    }: {
      taskId: string;
      completed: boolean;
      force?: boolean;
    }) =>
      completed
        ? http.post<TaskDetail>(`/tasks/${taskId}/complete`, { force })
        : http.post<TaskDetail>(`/tasks/${taskId}/uncomplete`),
    onMutate: async ({ taskId, completed }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.task(taskId) });
      const previous = queryClient.getQueryData<TaskDetail>(queryKeys.task(taskId));
      if (previous) {
        queryClient.setQueryData<TaskDetail>(queryKeys.task(taskId), {
          ...previous,
          completedAt: completed ? new Date().toISOString() : null,
        });
      }
      return { previous };
    },
    onError: (error: Error, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.task(variables.taskId), context.previous);
      }
      // A blocked card is not an error to shout about — the caller shows the
      // §6.5 "complete anyway?" confirmation instead.
      if (error instanceof ApiError && error.details?.reason === 'blocked') return;
      toast.error(error.message);
    },
    onSettled: (_data, _error, variables) => invalidate(variables.taskId),
  });
}

export function useDeleteTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (taskId: string) => http.delete<void>(`/tasks/${taskId}`),
    onSuccess: async () => {
      await invalidate();
      toast.success('Card deleted');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useSetTags(taskId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (tagIds: string[]) => http.put<TaskDetail>(`/tasks/${taskId}/tags`, { tagIds }),
    onSuccess: async (task) => {
      queryClient.setQueryData(queryKeys.task(taskId), task);
      await invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useSetCustomField(taskId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ fieldId, value }: { fieldId: string; value: unknown }) =>
      http.put<TaskDetail>(`/tasks/${taskId}/fields/${fieldId}`, { value }),
    onSuccess: async (task) => {
      queryClient.setQueryData(queryKeys.task(taskId), task);
      await invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Update any card, given its id at call time.
 *
 * `useUpdateTask` is bound to one card, which is right for the panel and wrong
 * for a board: a kanban drag or a calendar drop knows which card only when it
 * happens. This carries no `lastSeenUpdatedAt` — a drag is a deliberate
 * single-field move, and a 409 on "I dropped it there" would be noise, not
 * protection (§6.2's conflict story is about the panel's field grid).
 */
export function useUpdateAnyTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ taskId, ...patch }: UpdateTaskRequest & { taskId: string }) =>
      http.patch<TaskDetail>(`/tasks/${taskId}`, patch),
    onSuccess: (task) => invalidate(task.id),
    onError: (error: Error) => toast.error(error.message),
  });
}

/** As above, for a custom field — the kanban `customField:` column source. */
export function useSetAnyCustomField() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({ taskId, fieldId, value }: { taskId: string; fieldId: string; value: unknown }) =>
      http.put<TaskDetail>(`/tasks/${taskId}/fields/${fieldId}`, { value }),
    onSuccess: (task) => invalidate(task.id),
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useUpdateMetadata(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMetadataRequest) =>
      http.put<TaskDetail>(`/tasks/${taskId}/metadata`, input),
    onSuccess: (task) => queryClient.setQueryData(queryKeys.task(taskId), task),
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useSetTaskProjects(taskId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (input: SetTaskProjectsRequest) =>
      http.put<TaskDetail>(`/tasks/${taskId}/projects`, input),
    onSuccess: async (task) => {
      queryClient.setQueryData(queryKeys.task(taskId), task);
      await invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Move one card to a new position (and optionally section) inside a project —
 * the list's drag and drop (#6, §6.3.1).
 *
 * Unlike `useSetTaskProjects` the card is an argument rather than a hook
 * parameter, because in a list any row may be the one that moved.
 *
 * `PUT /tasks/:id/projects` replaces *every* membership, so the card's other
 * projects have to be sent back unchanged — a reorder inside one project must
 * not quietly evict the card from another. The service compares against what
 * it finds and audits only what actually moved, so the untouched rows cost
 * nothing and produce no feed noise.
 */
export function useReorderTaskInProject() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: ({
      task,
      projectId,
      sectionId,
      position,
    }: {
      task: TaskSummary;
      projectId: string;
      sectionId: string | null;
      position: string;
    }) =>
      http.put<TaskDetail>(`/tasks/${task.id}/projects`, {
        memberships: task.projects.map((entry) =>
          entry.project.id === projectId
            ? { projectId, sectionId, position }
            : {
                projectId: entry.project.id,
                sectionId: entry.sectionId,
                position: entry.position,
              },
        ),
      } satisfies SetTaskProjectsRequest),
    // Not `onSuccess: invalidate` — that would infer the mutation's data type
    // from `invalidate`'s optional taskId parameter.
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useDependencies(taskId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();

  const settle = async (task: TaskDetail): Promise<void> => {
    queryClient.setQueryData(queryKeys.task(taskId), task);
    await invalidate();
  };

  const add = useMutation({
    mutationFn: (dependsOnId: string) =>
      http.post<TaskDetail>(`/tasks/${taskId}/dependencies`, { dependsOnId }),
    onSuccess: settle,
    onError: (error: Error) => {
      // §6.5 — the cycle path is the useful part of the message; show it.
      toast.error(error.message);
    },
  });

  const remove = useMutation({
    // A DELETE with a body: an edge is identified by both of its ends.
    mutationFn: (dependsOnId: string) =>
      http.delete<TaskDetail>(`/tasks/${taskId}/dependencies`, { dependsOnId }),
    onSuccess: settle,
    onError: (error: Error) => toast.error(error.message),
  });

  return { add, remove };
}

export function useCollaborators(taskId: string) {
  const queryClient = useQueryClient();
  const settle = (task: TaskDetail): void => {
    queryClient.setQueryData(queryKeys.task(taskId), task);
  };

  const add = useMutation({
    mutationFn: (input: AddCollaboratorRequest) =>
      http.post<TaskDetail>(`/tasks/${taskId}/collaborators`, input),
    onSuccess: settle,
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (userId: string) =>
      http.delete<TaskDetail>(`/tasks/${taskId}/collaborators/${userId}`),
    onSuccess: settle,
    onError: (error: Error) => toast.error(error.message),
  });

  return { add, remove };
}

/**
 * §6.16 — "Mark as duplicate of #X … Fully reversible (un-cancel + delete
 * nothing)", which is why the toast says so.
 */
export function useMarkDuplicate(taskId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (ofTaskId: string) =>
      http.post<TaskDetail>(`/tasks/${taskId}/mark-duplicate`, { ofTaskId }),
    onSuccess: async (task) => {
      queryClient.setQueryData(queryKeys.task(taskId), task);
      await invalidate();
      toast.success('Merged — nothing was moved, and it can be undone');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * Undo a merge — card #22.
 *
 * Invalidates both cards: the duplicate reopens, and the survivor's "Merged
 * from" list just lost a row.
 */
export function useUnmarkDuplicate(taskId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: () => http.post<TaskDetail>(`/tasks/${taskId}/unmark-duplicate`, {}),
    onSuccess: async (task) => {
      queryClient.setQueryData(queryKeys.task(taskId), task);
      await invalidate();
      toast.success('Reopened — no longer a duplicate');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * My Tasks snooze — R42, §7.5.
 *
 * "affects only My Tasks grouping, never the card itself or other views", and
 * "Due-date notifications ignore snooze — hiding a card is not permission to
 * miss it." Both of those are server-side guarantees; this is just the call.
 */
export function useSnooze() {
  const invalidate = useInvalidateTasks();
  return {
    snooze: useMutation({
      mutationFn: ({ taskId, until }: { taskId: string; until: string }) =>
        http.put<void>(`/tasks/${taskId}/snooze`, { until }),
      onSuccess: async (_data, variables) => {
        await invalidate(variables.taskId);
        toast.success(`Snoozed until ${variables.until}`);
      },
      onError: (error: Error) => toast.error(error.message),
    }),
    unsnooze: useMutation({
      mutationFn: (taskId: string) => http.delete<void>(`/tasks/${taskId}/snooze`),
      onSuccess: async (_data, taskId) => {
        await invalidate(taskId);
      },
      onError: (error: Error) => toast.error(error.message),
    }),
  };
}

/** §6.16 — advisory duplicate suggestions while a title is being typed. */
export function useSimilarTasks(title: string, enabled: boolean): UseQueryResult<TaskRef[], Error> {
  return useQuery({
    queryKey: queryKeys.similarTasks(title),
    queryFn: () => http.get<TaskRef[]>('/tasks/similar', { query: { title } }),
    enabled: enabled && title.trim().length >= 3,
    staleTime: 30_000,
  });
}
